import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  const hotArticles = useMemo(() => {
    return [...articles]
      .filter((a) => a.slug !== slug)
      .sort((a, b) => b.readCount - a.readCount)
      .slice(0, 5);
  }, [slug]);

  const formatCount = (count: number): string => {
    if (count >= 10000) return (count / 10000).toFixed(1) + '万';
    return count.toString();
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章不存在</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被删除，请返回首页查看更多爆料</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '吃瓜51',
    },
    url: `https://chigua51.com/article/${slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article>
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: article.categoryName, path: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <header className="mt-6 mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium text-xs hover:bg-primary-200 transition-colors no-underline whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatCount(article.readCount)} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {formatCount(article.commentCount)} 评论
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </header>

              <div className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>

              <div
                className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-thumb-up-line"></i> 点赞
                    </button>
                    <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line"></i> 分享
                    </button>
                    <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-bookmark-line"></i> 收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <div className="bg-background-100 py-10">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                  {relatedArticles.length > 0 && (
                    <>
                      <h3 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2" style={{ fontFamily: "var(--font-heading)" }}>
                        <i className="ri-links-line text-primary-500"></i>
                        相关爆料
                      </h3>
                      <div className="space-y-3">
                        {relatedArticles.map((ra) => (
                          <ArticleCard
                            key={ra.id}
                            variant="compact"
                            slug={ra.slug}
                            title={ra.title}
                            excerpt={ra.excerpt}
                            category={ra.category}
                            categoryName={ra.categoryName}
                            publishDate={ra.publishDate}
                            readCount={ra.readCount}
                            commentCount={ra.commentCount}
                            imageUrl={ra.imageUrl}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <aside className="lg:w-80 flex-shrink-0">
                  <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-fire-line text-primary-500"></i>
                      热门爆料
                    </h4>
                    <div className="space-y-3">
                      {hotArticles.map((ha, index) => (
                        <Link
                          key={ha.id}
                          to={`/article/${ha.slug}`}
                          className="flex items-start gap-2.5 no-underline group"
                        >
                          <span className="w-5 h-5 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 mt-0.5 bg-primary-100 text-primary-600">
                            {index + 1}
                          </span>
                          <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                            {ha.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}